import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Currency } from "../../../../interfaces/screens/billing";
import { fetchCurrencies } from "./actions";
import { storage } from "../../../../shared/utils/storage";

interface CurrenciesState {
  data: Currency[];
  loading: boolean;
  error: string | null;
}

const initialState: CurrenciesState = {
  data: [],
  loading: false,
  error: null,
};

const currenciesSlice = createSlice({
  name: "currencies",
  initialState,
  reducers: {
    addCurrency: (state, action: PayloadAction<Currency>) => {
      if (action.payload.is_default) {
        state.data.forEach(currency => {
          currency.is_default = false;
        });
      }
      state.data.push(action.payload);
    },
    updateCurrency: (state, action: PayloadAction<Currency>) => {
      if (action.payload.is_default) {
        state.data.forEach(currency => {
          currency.is_default = false;
        });
      }
      const index = state.data.findIndex(
        (c) => c.code === action.payload.code,
      );
      if (index !== -1) {
        state.data[index] = action.payload;
      }
    },
    deleteCurrency: (state, action: PayloadAction<string>) => {
      state.data = state.data.filter((c) => c.code !== action.payload);
    },
    setDefaultCurrency: (state, action: PayloadAction<string>) => {
      const currencyCode = action.payload;
      state.data = state.data.map((currency) => ({
        ...currency,
        is_default: currency.code === currencyCode,
      }));
      storage.set("defaultCurrencyCode", currencyCode);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrencies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCurrencies.fulfilled,
        (state, action: PayloadAction<Currency[]>) => {
          state.loading = false;
          let currencies = action.payload;
          const defaultCode = storage.get("defaultCurrencyCode", null);

          if (defaultCode) {
            currencies = currencies.map((c) => ({
              ...c,
              is_default: c.code === defaultCode,
            }));
          } else if (currencies.length > 0 && !currencies.some(c => c.is_default)) {
            // If no default is set from storage or API, set the first one as default
            currencies[0].is_default = true;
            storage.set("defaultCurrencyCode", currencies[0].code);
          }
          
          state.data = currencies;
        },
      )
      .addCase(fetchCurrencies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch currencies";
      });
  },
});

export const {
  addCurrency,
  updateCurrency,
  deleteCurrency,
  setDefaultCurrency,
} = currenciesSlice.actions;

export default currenciesSlice.reducer; 